import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  name: "", specialization: "", department: "", phone: "", email: "",
  consultation_fee: "", slot_duration: 30, is_active: true, service_ids: [],
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</Label>
    {children}
  </div>
);

export function DoctorFormDialog({ open, onOpenChange, doctor, services = [], onSaved }) {
  const isEdit = Boolean(doctor);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setErr("");
      if (doctor) setForm({
        name: doctor.name || "", specialization: doctor.specialization || "",
        department: doctor.department || "", phone: doctor.phone || "", email: doctor.email || "",
        consultation_fee: doctor.consultation_fee ?? "", slot_duration: doctor.slot_duration || 30,
        is_active: doctor.is_active ?? true, service_ids: doctor.service_ids || [],
      });
      else setForm(EMPTY);
    }
  }, [open, doctor]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (id) => set("service_ids",
    form.service_ids.includes(id) ? form.service_ids.filter((x) => x !== id) : [...form.service_ids, id]);

  const submit = async () => {
    if (!form.name.trim()) { setErr("Doctor name is required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      consultation_fee: form.consultation_fee === "" ? null : parseFloat(form.consultation_fee),
      slot_duration: parseInt(form.slot_duration, 10),
    };
    try {
      if (isEdit) await api.put(`/doctors/${doctor.id}`, payload);
      else await api.post("/doctors", payload);
      toast.success(isEdit ? "Doctor updated" : "Doctor added");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="doctor-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Doctor" : "Add Doctor"}</DialogTitle>
          <DialogDescription>Configure the practitioner profile. Working hours are managed on the doctor detail page.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 py-2 sm:grid-cols-2">
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="doctor-input-name" />
          </Field>
          <Field label="Specialization"><Input value={form.specialization} onChange={(e) => set("specialization", e.target.value)} /></Field>
          <Field label="Department"><Input value={form.department} onChange={(e) => set("department", e.target.value)} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
          <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Consultation fee (₹)"><Input type="number" value={form.consultation_fee} onChange={(e) => set("consultation_fee", e.target.value)} /></Field>
          <Field label="Slot duration (min)"><Input type="number" value={form.slot_duration} onChange={(e) => set("slot_duration", e.target.value)} data-testid="doctor-input-slot" /></Field>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="doctor-active-switch" />
            <Label>Active</Label>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Services offered</Label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {services.map((s) => (
                <label key={s.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm">
                  <Checkbox checked={form.service_ids.includes(s.id)} onCheckedChange={() => toggleService(s.id)} />
                  {s.name}
                </label>
              ))}
            </div>
          </div>
          {err && <p className="text-sm text-red-600 sm:col-span-2">{err}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="doctor-form-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{isEdit ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
